// ConfirmDialog — the reusable destructive-confirm shell for the OS.
//
// One dialog the whole app uses for risky actions: archive, merge,
// deactivate, restore, delete. The archive-household flow is its first
// consumer (see HouseholdPage); Merge / Deactivate / Restore adopt it
// next by passing different copy + permission.
//
// It is a thin shell over the design-system <Modal> primitive (ui.tsx),
// so it inherits, for free: portal-to-body, focus trap, focus-return to
// the trigger on close, Escape-to-close, scroll lock, the navy backdrop,
// and the aria-modal / aria-labelledby wiring. We only restyle the
// header to sentence-case (the Modal default is uppercase) and add the
// four net-new pieces the reference calls for: the neutral info panel,
// the reversible note, the gated reason field, and the denied state.
//
// TWO STATES, DECIDED BY PERMISSION UP FRONT (see confirm-dialog-helpers):
//   * confirm — explanation + optional reason + Cancel/destructive button
//   * denied  — a blocked state (403): error banner, plain "why", what to
//               do instead, and ONLY a Close button. We never render a
//               form the user can't submit. The caller decides `allowed`
//               from the user's real permission BEFORE this renders.
//
// A 403 returned ON SUBMIT (a race) is handled by the caller passing the
// resulting message in via `error`, which renders as an inline banner in
// the confirm state — never a silent failure.
//
// Tokens only: color touches the icon badge, the error banner, the
// destructive button, and the info-panel dots — never small body text.
// No raw hex here; everything reads semantic CSS tokens, so dark mode
// comes for free.

import { useEffect, useId, useState, type ReactNode } from "react";
import { RotateCcw, ShieldAlert } from "lucide-react";

import { Button, Modal } from "../ui";
import {
  canSubmitConfirm,
  resolveConfirmMode,
  type ConfirmDialogMode,
} from "./confirm-dialog-helpers";

/**
 * The permission decision + the denied-state copy. When `allowed` is
 * false the dialog renders the blocked state from these fields. Decide
 * `allowed` from the user's REAL permission (session role / server-known
 * scope), not after they try.
 */
export interface ConfirmPermission {
  /** False → render the denied state instead of the confirm form. */
  allowed: boolean;
  /** Bold line in the error banner. Defaults to "Access denied (403)". */
  statusLabel?: string;
  /** One-line summary under the status label in the error banner. */
  summary?: ReactNode;
  /** Plain-English paragraph: why this is restricted. */
  explanation?: ReactNode;
  /** "What to do instead" bullets — keeps the denied state from dead-ending. */
  whatToDo?: ReadonlyArray<ReactNode>;
}

export interface ConfirmDialogProps {
  /** Open flag — nothing renders when false. */
  open: boolean;

  /** Sentence-case title (e.g. "Archive Keith Lytle"). No all-caps. */
  title: string;
  /** Small line under the title (e.g. "Household · Stewardship Mortgage"). */
  subtitle?: ReactNode;
  /** Neutral icon badge left of the title. Pass a 24-viewBox lucide icon. */
  icon?: ReactNode;

  /** Permission decision + denied-state copy. See ConfirmPermission. */
  permission: ConfirmPermission;

  /** Optional lede paragraph above the info panel (confirm state). */
  lede?: ReactNode;
  /** Info-panel heading. Defaults to "What this does". */
  infoTitle?: string;
  /** Plain-English "what this does" bullets. Rendered as the info panel. */
  consequences: ReadonlyArray<ReactNode>;

  /** Optional reversible note (with a restore icon) under the info panel. */
  reversibleNote?: ReactNode;

  /** Show the reason field. Defaults to true. */
  showReasonField?: boolean;
  /** Require a reason — gates the destructive button. Defaults to true. */
  reasonRequired?: boolean;
  /** Reason field label. Defaults to "Reason". */
  reasonLabel?: string;
  /** Reason field hint. Defaults to the audit-log note. */
  reasonHint?: ReactNode;
  reasonPlaceholder?: string;
  /** Pre-filled reason. Reset to this whenever the dialog (re)opens. */
  initialReason?: string;

  /** Destructive button label (e.g. "Archive household"). */
  confirmLabel: string;
  /** Optional icon left of the destructive button label. */
  confirmIcon?: ReactNode;

  /** Cancel button label (confirm state). Defaults to "Cancel". */
  cancelLabel?: string;
  /** Close button label (denied state). Defaults to "Close". */
  closeLabel?: string;

  /** External running flag — locks both buttons + the close affordances. */
  running?: boolean;
  /** Inline error banner shown above the explanation (e.g. submit-time 403). */
  error?: string | null;

  /** Called with the reason text when the destructive button is clicked. */
  onConfirm: (reason: string) => void;
  /** Called when the dialog is dismissed (Cancel / Close / Esc / backdrop). */
  onClose: () => void;
}

export function ConfirmDialog({
  open,
  title,
  subtitle,
  icon,
  permission,
  lede,
  infoTitle = "What this does",
  consequences,
  reversibleNote,
  showReasonField = true,
  reasonRequired = true,
  reasonLabel = "Reason",
  reasonHint = "Required, and saved to the audit log.",
  reasonPlaceholder,
  initialReason = "",
  confirmLabel,
  confirmIcon,
  cancelLabel = "Cancel",
  closeLabel = "Close",
  running = false,
  error = null,
  onConfirm,
  onClose,
}: ConfirmDialogProps) {
  const [reason, setReason] = useState(initialReason);
  const reasonId = useId();
  const errorId = useId();

  const mode: ConfirmDialogMode = resolveConfirmMode(permission.allowed);

  // Reset the typed reason whenever the dialog opens (or its target's
  // initialReason changes) — without this, reopening for a different
  // target shows the previous target's reason. Same fix as
  // DestructiveActionConfirm (2026-05-29).
  useEffect(() => {
    if (open) setReason(initialReason);
  }, [open, initialReason]);

  const canSubmit = canSubmitConfirm({
    allowed: permission.allowed,
    running,
    reasonRequired: showReasonField && reasonRequired,
    reason,
  });

  // Don't let a stuck `running` flag trap the user when nothing is in
  // flight; close is always available except mid-run.
  const handleClose = running ? () => undefined : onClose;

  const footer =
    mode === "confirm" ? (
      <div className="confirm-dialog__footer">
        <Button variant="sw-default" onClick={handleClose} disabled={running}>
          {cancelLabel}
        </Button>
        <Button
          variant="sw-danger"
          onClick={() => onConfirm(reason)}
          disabled={!canSubmit}
          iconLeft={confirmIcon}
        >
          {running ? "Working…" : confirmLabel}
        </Button>
      </div>
    ) : (
      <div className="confirm-dialog__footer confirm-dialog__footer--denied">
        <Button variant="sw-default" onClick={onClose}>
          {closeLabel}
        </Button>
      </div>
    );

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={title}
      sub={subtitle}
      icon={icon}
      size="md"
      className="confirm-dialog"
      footer={footer}
      dismissOnBackdrop={!running}
      dismissOnEsc={!running}
    >
      {mode === "confirm" ? (
        <div className="confirm-dialog__body">
          {error && (
            <ErrorBar
              id={errorId}
              statusLabel="Couldn't complete that"
              summary={error}
            />
          )}

          {lede && <p className="confirm-dialog__lede">{lede}</p>}

          <section className="confirm-info" aria-label={infoTitle}>
            <div className="confirm-info__title">{infoTitle}</div>
            <ul className="confirm-info__list">
              {consequences.map((line, i) => (
                <li className="confirm-info__item" key={i}>
                  <span className="confirm-info__dot" aria-hidden="true" />
                  <span>{line}</span>
                </li>
              ))}
            </ul>
            {reversibleNote && (
              <div className="confirm-info__revert">
                <RotateCcw
                  className="confirm-info__revert-icon"
                  aria-hidden="true"
                />
                <span>{reversibleNote}</span>
              </div>
            )}
          </section>

          {showReasonField && (
            <div className="confirm-dialog__field">
              <label htmlFor={reasonId} className="confirm-dialog__label">
                {reasonLabel}
              </label>
              {reasonHint && (
                <div className="confirm-dialog__hint">{reasonHint}</div>
              )}
              <textarea
                id={reasonId}
                className="confirm-dialog__textarea"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder={reasonPlaceholder}
                disabled={running}
                rows={3}
              />
            </div>
          )}
        </div>
      ) : (
        <div className="confirm-dialog__body">
          <ErrorBar
            id={errorId}
            statusLabel={permission.statusLabel ?? "Access denied (403)"}
            summary={
              permission.summary ??
              "You don't have permission to take this action."
            }
          />
          {permission.explanation && (
            <p className="confirm-dialog__lede">{permission.explanation}</p>
          )}
          {permission.whatToDo && permission.whatToDo.length > 0 && (
            <section className="confirm-info" aria-label="What to do">
              <div className="confirm-info__title">What to do</div>
              <ul className="confirm-info__list">
                {permission.whatToDo.map((line, i) => (
                  <li className="confirm-info__item" key={i}>
                    <span className="confirm-info__dot" aria-hidden="true" />
                    <span>{line}</span>
                  </li>
                ))}
              </ul>
            </section>
          )}
        </div>
      )}
    </Modal>
  );
}

// ─── Error banner (denied + inline submit-error) ────────────────────────
//
// A structured banner: red icon + red left border carry the color; the
// title + body stay in neutral text tokens (small colored body text
// fails contrast and is banned by the design guide).

function ErrorBar({
  id,
  statusLabel,
  summary,
}: {
  id?: string;
  statusLabel: ReactNode;
  summary: ReactNode;
}) {
  return (
    <div className="confirm-errbar" role="alert" id={id}>
      <ShieldAlert className="confirm-errbar__icon" aria-hidden="true" />
      <div>
        <div className="confirm-errbar__title">{statusLabel}</div>
        <div className="confirm-errbar__body">{summary}</div>
      </div>
    </div>
  );
}
